// RN-279: 管理画面の RMS手動DL で、押した手動取得が終わったかを見分ける（settings/rmsManual の記録を読む）

// 記録の日時（Firestore の Timestamp は {_seconds, _nanoseconds} で届く）をミリ秒に
export function toMillis(v: any): number | null {
    if (v == null) return null;
    if (typeof v === "number") return v;
    if (typeof v === "string") {
        const ms = Date.parse(v);
        return Number.isFinite(ms) ? ms : null;
    }
    const s = v._seconds ?? v.seconds;
    if (typeof s === "number") return s * 1000 + Math.floor(Number(v._nanoseconds ?? v.nanoseconds ?? 0) / 1e6);
    return null;
}

// 押した手動取得が終わったか。記録は前の回の値も残る（running と開始時刻を足すだけ）ので、押した時刻より後に終わった手動の回だけを見る。
//   成功は、手動の回の runId（"rms-donwload__"＝ apps/api/src/routes/super/rmsDownload.ts の runIdNow）のときだけ。
//   失敗は runId を見ない（取得の仕組みの起動の失敗は runId を書かない＝ rmsDownload.ts:142-149）。
//   夜中の取り直しなど、ほかの回が書いた結果は、期間（startDate・endDate）が合わないので pickFinishedWatch で外れる
export function finishedManualRun(data: any, sinceMs: number): "success" | "error" | null {
    const st = String(data?.status ?? "");
    if (st !== "success" && st !== "error") return null;
    if (st === "success" && !String(data?.runId ?? "").startsWith("rms-donwload__")) return null;
    const endedMs = toMillis(data?.endedAt);
    if (endedMs == null || endedMs < sinceMs) return null;
    // 押したときに記録の開始（startedAt）が新しくなる。終わり（endedAt）がそれより前なら前の回の残り（どちらもサーバーの時計）
    const startedMs = toMillis(data?.startedAt);
    if (startedMs != null && endedMs < startedMs) return null;
    return st;
}

// 押した回の控え（同じお店で、終わる前に続けて押すこともあるので、押した回ごとに持つ）
export type ManualRunWatch = {
    sinceMs: number; // 押した時刻の1分前（パソコンとサーバーの時計のずれを見込む）
    pressedAtMs: number; // 押した時刻
    target: string; // どの会社のどのお店の回か（tenantId / accountId / shopUrl）
    shopUrl: string;
    startDate: string;
    endDate: string;
    afterPollSeq: number;
    stuckNotified?: boolean; // 「終わっていません」を知らせ済み
    errorNotified?: boolean; // 「失敗しました」を知らせ済み（取得は自動でやり直すことがあり、あとで成功を書くことがある）
};

// どの会社のどのお店か（同じお店の URL で別の会社の記録と取り違えないため）
export function watchTarget(tenantId: string, accountId: string, shopUrl: string): string {
    return `${tenantId}/${accountId}/${shopUrl}`;
}

// 押してから、終わった記録が無いまま limitMs を過ぎた回（途中で止まった可能性）。
//   いま見ているお店（target）の回だけ（ほかのお店の記録は読んでいないので、終わったかが分からない）。知らせ済みの回は除く
export function stuckWatchIndexes(watches: ManualRunWatch[], target: string, nowMs: number, limitMs: number): number[] {
    const out: number[] = [];
    watches.forEach((w, i) => {
        if (w.target === target && !w.stuckNotified && !w.errorNotified && nowMs - w.pressedAtMs >= limitMs) out.push(i);
    });
    return out;
}

// 読んだ記録が、どの押した回の終わりかを選ぶ（見つからなければ -1）
//   ・押したあとに読み始めた記録だけを見る（押す前に読み始めて押したあとに返ってきた古い記録を、今回の結果にしない）
//   ・取得は終わったときに自分の期間（startDate・endDate）を記録に書く＝期間が合う回に結びつける
//   ・同じ期間で2回押したときは、先に押した回から
//   ・失敗を知らせ済みの回は、失敗の記録では選ばない（同じ失敗を何度も知らせない）。あとで成功の記録が来たら選ぶ
export function pickFinishedWatch(data: any, target: string, pollSeq: number, watches: ManualRunWatch[]): number {
    return watches.findIndex((w) => {
        if (w.target !== target || pollSeq <= w.afterPollSeq) return false;
        if (String(data?.startDate ?? "") !== w.startDate || String(data?.endDate ?? "") !== w.endDate) return false;
        const done = finishedManualRun(data, w.sinceMs);
        if (done === null) return false;
        if (done === "error" && w.errorNotified) return false;
        return true;
    });
}

